import { useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import { getSessionLockService } from '../../../api/sessionLockService';
import { logger } from '../../../utils/logger';

const CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes
const IDLE_THRESHOLD = 2 * 60 * 1000; // 2 minutes

/**
 * Hook that auto-extends the session lock when the user is actively working.
 *
 * Runs every 2 minutes. On each tick it checks whether the user has been
 * active (mouse, keyboard, touch, scroll, click) within the last 2 minutes.
 *
 *  - Active  → silently calls `refreshLockExpiration()` (same as "Continue
 *    Editing" button) which sends heartbeat, resets all timers, and extends
 *    the lock for another full DLO_LOCK_TIMEOUT_MINUTES period.
 *  - Idle    → skips. The next tick (2 min later) checks again.
 *
 * This keeps the session alive indefinitely for active users while still
 * allowing the warning popup to appear for idle users.
 */
export function useAutoExtendLock() {
  const lastActivityRef = useRef<number>(Date.now());

  // ---- track user activity ------------------------------------------------
  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // ---- auto-extend logic (runs every 2 minutes) ---------------------------
  const checkAndExtend = useCallback(async () => {
    const idleMs = Date.now() - lastActivityRef.current;

    if (idleMs > IDLE_THRESHOLD) {
      logger.log(`[useAutoExtendLock] User idle for ${Math.round(idleMs / 1000)}s — skipping`);
      return;
    }

    const lockService = getSessionLockService();
    if (!lockService) return;

    const activeLocks = lockService.getActiveLocks();
    if (activeLocks.size === 0) return;

    for (const [resourceId, lockData] of activeLocks.entries()) {
      try {
        // Get the latest lock data (token may have been refreshed by heartbeat)
        const currentLockData = lockService.getActiveLock(resourceId);
        if (!currentLockData) continue;

        logger.log(`[useAutoExtendLock] User active — extending lock for ${resourceId}`);
        await lockService.refreshLockExpiration(resourceId, currentLockData.lockToken);
        logger.log(`[useAutoExtendLock] Lock extended successfully for ${resourceId}`);
      } catch (err) {
        logger.warn(`[useAutoExtendLock] Failed to extend lock for ${resourceId}:`, err);
      }
    }
  }, []);

  // ---- lifecycle -----------------------------------------------------------
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const events: (keyof WindowEventMap)[] = [
      'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
    ];

    events.forEach(e => window.addEventListener(e, markActivity, { passive: true }));

    const intervalId = setInterval(checkAndExtend, CHECK_INTERVAL);

    return () => {
      events.forEach(e => window.removeEventListener(e, markActivity));
      clearInterval(intervalId);
    };
  }, [markActivity, checkAndExtend]);
}
